import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SidebarService } from '../../../services/sidebar/sidebar.service';
import { OrganizationFilterService } from '../../../services/organization-filter/organization-filter.service';
import { UsdObservedRateService, UsdObservedRateSnapshot } from '../../../services/usd-observed-rate/usd-observed-rate.service';
import { ProductCatalogService } from '../../../services/product-catalog/product-catalog.service';
import { ProductModuleId } from '../../../services/product-catalog/product-catalog.data';
import { Organization } from '../../../interfaces/organization';
import { FilterPanelComponent } from '../filter-panel/filter-panel.component';

/** Solo se usa para rotular el contexto: el cambio de producto vive en el sidebar. */
interface HeaderProductOption {
  id: string;
  label: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  pageTitle = 'Inicio';
  pageSubtitle = 'Metabridge';
  selectedProductId = '';
  currentProductModule?: ProductModuleId;
  readonly productOptions: HeaderProductOption[];
  contextPanelVisible = false;
  selectedOrganizationIds: string[] = [];
  draftOrganizationIds: string[] = [];
  organizations: Organization[] = [];
  organizationsLoading = false;
  hasPlatformAdminAccess = false;
  userOrganizationId: string | null = null;

  private rateSnapshot: UsdObservedRateSnapshot = { rate: null, rateDate: '', rateTimestamp: '', loading: false };
  private rateSubscription?: Subscription;
  private navigationSubscription?: Subscription;
  private contextSubscriptions: Subscription[] = [];
  @ViewChild('contextFilterPanel') private contextFilterPanel?: FilterPanelComponent;

  constructor(
    private router: Router,
    public sidebarService: SidebarService,
    private organizationFilterService: OrganizationFilterService,
    private usdObservedRateService: UsdObservedRateService,
    private productCatalogService: ProductCatalogService
  ) {
    const preferredOrder = ['airnode', 'bin-tracking', 'birdattack', 'lelfunko', 'raidar', 'seppia'];
    this.productOptions = preferredOrder.flatMap((id) => {
      const entry = this.productCatalogService.getEntry(id);
      if (!entry) return [];
      const labels: Record<string, string> = { birdattack: 'Bird Attack', seppia: 'SepIA' };
      return [{ id, label: labels[id] ?? entry.displayName }];
    });
  }

  get isProductContext(): boolean {
    return Boolean(this.selectedProductId);
  }

  get showUsdObservedRateIndicator(): boolean {
    return this.usdObservedRateService.enabled;
  }

  get usdObservedRateLoading(): boolean {
    return this.rateSnapshot.loading;
  }

  get usdObservedRateDisplay(): string {
    return this.rateSnapshot.rate ? this.usdObservedRateService.formatClp(this.rateSnapshot.rate) : '—';
  }

  get usdObservedRateTooltip(): string {
    if (this.rateSnapshot.loading) {
      return 'Dólar observado\n\nConsultando indicador...';
    }

    if (!this.rateSnapshot.rate) {
      return 'Dólar observado\n\nSin información disponible.';
    }

    return [
      'Dólar observado',
      '',
      this.rateSnapshot.rateDate ? `${this.rateSnapshot.rateDate}` : null,
      this.rateSnapshot.rate ? `${this.usdObservedRateDisplay}` : null,
    ]
      .filter((line): line is string => Boolean(line))
      .join('\n');
  }

  ngOnInit() {
    this.updatePageContext(this.router.url);
    this.navigationSubscription = this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
      this.updatePageContext(event.urlAfterRedirects);
    });

    this.usdObservedRateService.load();
    this.rateSubscription = this.usdObservedRateService.state$.subscribe((snapshot) => {
      this.rateSnapshot = snapshot;
    });

    this.contextSubscriptions.push(
      this.organizationFilterService.selectedOrgIds$.subscribe((ids) => {
        this.selectedOrganizationIds = [...ids];
        if (!this.contextPanelVisible) this.draftOrganizationIds = [...ids];
      }),
      this.organizationFilterService.organizations$.subscribe((organizations) => {
        this.organizations = organizations;
        this.userOrganizationId = this.organizationFilterService.getUserOrganizationIdSync();
      }),
      this.organizationFilterService.loading$.subscribe((loading) => (this.organizationsLoading = loading)),
      this.organizationFilterService.hasPlatformAdminAccessObs$.subscribe((allowed) => {
        this.hasPlatformAdminAccess = allowed;
        this.userOrganizationId = this.organizationFilterService.getUserOrganizationIdSync();
      })
    );
  }

  ngOnDestroy(): void {
    this.rateSubscription?.unsubscribe();
    this.navigationSubscription?.unsubscribe();
    this.contextSubscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  redirectHome() {
    this.router.navigate(['home']);
  }

  toggleSidebar() {
    this.sidebarService.toggle();
  }

  /**
   * PrimeNG emite `null` al pulsar la X del multiselect (`clear()` hace `updateModel(null)`),
   * lo que rompía los resúmenes que leen `.length` y dejaba el drawer con datos obsoletos.
   */
  onDraftOrganizationsChange(organizationIds: string[] | null | undefined): void {
    this.draftOrganizationIds = Array.isArray(organizationIds) ? organizationIds : [];
  }

  /** El panel decide si se muestra como menú (desktop) o bottom drawer (mobile). */
  openContextFilters(event: Event): void {
    // Sin organizaciones cargadas el selector queda vacío y no habría nada que aplicar.
    void this.organizationFilterService.loadOrganizations();
    this.resetDraftContext();
    this.contextFilterPanel?.toggle(event);
  }

  onContextPanelVisibleChange(visible: boolean): void {
    this.contextPanelVisible = visible;
    if (!visible) this.resetDraftContext();
  }

  cancelContextChanges(): void {
    this.contextFilterPanel?.hide();
    this.resetDraftContext();
  }

  /** "Limpiar Todo": vuelve al contexto más amplio disponible sin cerrar el panel. */
  clearContextFilters(): void {
    this.draftOrganizationIds = [];
  }

  applyContextChanges(): void {
    const options = this.organizationOptions;
    const allowedOrganizationIds = new Set(options.map((organization) => organization.id).filter(Boolean));
    /*
     * El descarte contra las opciones evita aplicar ids obsoletos, pero si la
     * lista aún no llegó descartaría todo y el filtro parecería no funcionar:
     * en ese caso se aplica el borrador tal cual.
     */
    const organizationIds = options.length === 0 ? [...this.draftOrganizationIds] : this.draftOrganizationIds.filter((id) => allowedOrganizationIds.has(id));

    if (this.sameIds(organizationIds, this.selectedOrganizationIds)) return;
    this.organizationFilterService.setSelectedOrganizations(organizationIds);
  }

  private resetDraftContext(): void {
    this.draftOrganizationIds = [...this.selectedOrganizationIds];
  }

  /**
   * Las organizaciones llegan con `id` o solo con `orgId` según el origen del
   * registro; el selector necesita una única clave o la selección se pierde.
   */
  get organizationOptions(): Organization[] {
    const organizations = this.organizations
      .map((organization) => ({ ...organization, id: organization.id || organization.orgId }))
      .filter((organization) => Boolean(organization.id));
    if (this.hasPlatformAdminAccess || !this.userOrganizationId) return organizations;
    return organizations.filter((organization) => organization.id === this.userOrganizationId);
  }

  get hasContextChanges(): boolean {
    return !this.sameIds(this.draftOrganizationIds, this.selectedOrganizationIds);
  }

  get activeProductLabel(): string {
    return this.productOptions.find((option) => option.id === this.selectedProductId)?.label ?? 'Sin producto';
  }

  get organizationContextLabel(): string {
    if (this.selectedOrganizationIds.length === 0) return 'Todas las organizaciones';
    if (this.selectedOrganizationIds.length === 1) {
      const selectedId = this.selectedOrganizationIds[0];
      return this.organizations.find((organization) => (organization.id || organization.orgId) === selectedId)?.name ?? '1 organización';
    }
    return `${this.selectedOrganizationIds.length} organizaciones`;
  }

  get headerContextSubtitle(): string {
    if (!this.isProductContext) {
      const genericSubtitles = new Set(['Administración', 'Inventario', 'Cuenta', 'Metabridge']);
      return genericSubtitles.has(this.pageSubtitle) ? this.organizationContextLabel : `${this.pageSubtitle} - ${this.organizationContextLabel}`;
    }
    return `${this.activeProductLabel} - ${this.compactOrganizationContextLabel}`;
  }

  private get compactOrganizationContextLabel(): string {
    if (this.selectedOrganizationIds.length === 0) return 'Todos';
    if (this.selectedOrganizationIds.length > 1) return `${this.selectedOrganizationIds.length} organizaciones`;

    const label = this.organizationContextLabel.trim();
    return /^todos?(?:\s+(?:los|las))?(?:\s+(?:dispositivos|organizaciones))?$/i.test(label) ? 'Todos' : label;
  }

  private sameIds(left: string[], right: string[]): boolean {
    if (left.length !== right.length) return false;
    const rightIds = new Set(right);
    return left.every((id) => rightIds.has(id));
  }

  private updatePageContext(url: string): void {
    const segments = String(url ?? '')
      .split(/[?#]/, 1)[0]
      .split('/')
      .filter(Boolean)
      .map((segment) => decodeURIComponent(segment));

    if (segments[0] === 'home' && segments[1] === 'products') {
      this.selectedProductId = this.productCatalogService.canonicalServiceId(segments[2] ?? '');
      this.currentProductModule = (['devices', 'models', 'firmware', 'support', 'help'] as string[]).includes(segments[3])
        ? (segments[3] as ProductModuleId)
        : undefined;
      const product = this.productCatalogService.getDisplayName(segments[2] ?? '');
      const moduleTitles: Record<string, string> = {
        models: 'Modelos',
        devices: 'Dispositivos',
        firmware: 'Firmware',
        support: 'Tickets',
        help: 'Ayuda',
      };
      this.pageTitle = moduleTitles[segments[3]] ?? 'Resumen';
      this.pageSubtitle = product;
      return;
    }

    this.selectedProductId = '';
    this.currentProductModule = undefined;

    if (segments[0] === 'inventory') {
      const section = segments[1] ?? 'products';
      const inventoryTitles: Record<string, string> = {
        products: segments[2] === 'import' ? 'Importar insumos' : 'Insumos',
        boms: 'BOM',
        ubf: 'Catálogo UBF',
        suppliers: 'Proveedores',
        purchases: segments[2] === 'new' ? 'Nueva orden de compra' : segments[3] === 'edit' ? 'Editar orden de compra' : 'Órdenes de compra',
        stock: 'Control de stock',
        movements: 'Movimientos',
        adjustments: 'Ajustes de inventario',
      };
      this.pageTitle = inventoryTitles[section] ?? 'Inventario';
      this.pageSubtitle = section === 'boms' && segments[2] ? this.productCatalogService.getDisplayName(segments[2]) : 'Inventario';
      return;
    }

    if (segments[0] === 'administration') {
      this.pageTitle = segments[1] === 'organizations' ? 'Clientes' : 'Usuarios';
      this.pageSubtitle = 'Administración';
      return;
    }

    if (segments[0] === 'profile') {
      this.pageTitle = 'Mi perfil';
      this.pageSubtitle = 'Cuenta';
      return;
    }

    this.pageTitle = 'Inicio';
    this.pageSubtitle = 'Metabridge';
  }
}
